/**
 * Contracts Barrel — Public API for the contracts layer.
 *
 * Import all contract types from here:
 *   import { type DocumentDTO, type ApiError } from "@/contracts";
 *
 * NEVER import from individual contract files in UI code.
 */

// Shared primitives (errors, pagination, sorting)
export {
  // RFC 9457 Problem Details
  ProblemDetailSchema,
  type ProblemDetail,
  ProblemTypes,
  type ProblemTypeSlug,
  matchProblemType,
  ApiError,
  createApiError,

  // Pagination
  PaginationMetaSchema,
  type PaginationMeta,
  type PaginatedResponse,
  type PaginationParams,
  type CursorPaginationParams,
  type HateoasLinks,

  // Sorting
  type SortDirection,
  type SortParams,

  // Envelopes
  type ApiResponse,
  type Timestamps,
  type UUID,

  // Utilities
  buildMockHateoasLinks,
} from './shared';

// Collections domain
export {
  CollectionTypeEnum,
  type CollectionType,
  CollectionColorEnum,
  type CollectionColor,
  COLLECTION_COLORS,
  SortModeEnum,
  type SortMode,
  MAX_COLLECTION_DEPTH,
  type CollectionDTO,
  type CollectionTreeNode,
  type CollectionItemDTO,
  type CollectionItemsPage,
  type CreateCollectionRequest,
  type UpdateCollectionRequest,
  type AddItemsRequest,
  type RemoveItemsRequest,
  type ReorderItemsRequest,
  type AddDocumentToCollectionsRequest,
  type ICollectionsAdapter,
  normalizeTreeResponse,
  type PaginatedCollections,
  type CollectionListParams,
  type CollectionNoteDTO,
  type CollectionNotesPage,
} from './collections';

// Documents domain
export {
  DocumentFormatEnum,
  type DocumentFormat,
  DocumentStatusEnum,
  type DocumentStatus,
  PipelineStageEnum,
  type PipelineStage,
  StageStatusEnum,
  type StageStatus,
  PipelineStageStateSchema,
  type PipelineStageState,
  DocumentOwnerSchema,
  type DocumentOwner,
  DocumentDTOSchema,
  type DocumentDTO,
  type DocumentListFilters,
  type DocumentSortField,
  type DocumentListParams,
  type CreateDocumentRequest,
  type UpdateDocumentRequest,
  type DocumentListResponse,
  type DocumentDetailResponse,
  type CreateDocumentResponse,
  type UpdateDocumentResponse,
  type DeleteDocumentResponse,
  type RequestUploadUrlRequest,
  type RequestUploadUrlResponse,
  type RequestUploadUrlApiResponse,
  type RequestVersionUploadUrlRequest,
  type RequestVersionUploadUrlResponse,
  type ConfirmUploadRequest,
  type ConfirmUploadApiResponse,
  type DownloadUrlResponse,
  type IDocumentsAdapter,
  // Versioning
  type VersionOwner,
  type DocumentVersionDTO,
  type CreateVersionRequest,
  type DocumentVersionsResponse,
  // Comments
  type CommentAuthor,
  type DocumentCommentDTO,
  type CreateCommentRequest,
  type UpdateCommentRequest,
  type DocumentCommentsResponse,
  type CreateCommentResponse,
  type DeleteCommentResponse,
  // Artifacts
  ArtifactTypeEnum,
  type ArtifactType,
  type SectionContent,
  type TableContent,
  type FigureContent,
  type CitationContent,
  type ClaimContent,
  EvidenceTypeEnum,
  type EvidenceType,
  EffectDirectionEnum,
  type EffectDirection,
  ArtifactReadSchema,
  type ArtifactRead,
  type ArtifactListResponse,
  // Pipeline
  BackendPipelineStageEnum,
  type BackendPipelineStage,
  IngestionJobStepReadSchema,
  type IngestionJobStepRead,
  IngestionJobReadSchema,
  type IngestionJobRead,
  PipelineStatusReadSchema,
  type PipelineStatusRead,
  // Import / PDF
  type AttachPdfRequest,
  type DocumentImportResult,
  type ImportPreviewResult,
  type ImportConfirmResult,
  // AI Summary & Methodology
  type DocumentSummary,
  type StructuredSummary,
  type MethodologyScore,
  type BiasRisk,
  type QAPair,
  type DocumentAISummaryResponse,
  // Extended artifact content types
  type EquationContent,
  CitationContextTypeEnum,
  type CitationContextType,
  ProcessingProvenanceReadSchema,
  type ProcessingProvenanceRead,
  PerArtifactStatusReadSchema,
  type PerArtifactStatusRead,
} from './documents';

// Library domain
export {
  DocumentTypeEnum,
  type DocumentType,
  ProcessingStatusEnum,
  type ProcessingStatus,
  AuthorSchema,
  type Author,
  DocumentMetaSchema,
  type DocumentMeta,
  AISummarySchema,
  type AISummary,
  CitationSchema,
  type Citation,
  DocumentRelationshipSchema,
  type DocumentRelationship,
  LibraryDocumentDTOSchema,
  type LibraryDocumentDTO,
  LibraryCollectionDTOSchema,
  type LibraryCollectionDTO,
  type LibraryListFilters,
  type LibrarySortField,
  type LibraryListParams,
  type LibraryCreateCollectionRequest,
  type LibraryUpdateCollectionRequest,
  type LibraryListResponse,
  type LibraryDetailResponse,
  type LibraryCollectionListResponse,
  type LibraryCollectionDetailResponse,
  type LibraryCreateCollectionResponse,
  type LibraryUpdateCollectionResponse,
  type LibraryDeleteCollectionResponse,
  type ILibraryAdapter,
} from './library';

// Sessions domain
export {
  SessionStatusEnum,
  type SessionStatus,
  SessionPhaseEnum,
  type SessionPhase,
  SessionPriorityEnum,
  type SessionPriority,
  SessionCategoryEnum,
  type SessionCategory,
  SessionGoalSchema,
  type SessionGoal,
  SessionInsightSchema,
  type SessionInsight,
  InsightTypeEnum,
  type InsightType,
  ConfidenceLevelEnum,
  type ConfidenceLevel,
  SessionCollaboratorSchema,
  type SessionCollaborator,
  SessionSummaryDTOSchema,
  type SessionSummaryDTO,
  SessionDTOSchema,
  type SessionDTO,
  type SessionListFilters,
  type SessionSortField,
  type SessionListParams,
  type CreateSessionRequest,
  type UpdateSessionRequest,
  type AddGoalRequest,
  type UpdateGoalRequest,
  type AddInsightRequest,
  type SessionListResponse,
  type SessionDetailResponse,
  type CreateSessionResponse,
  type UpdateSessionResponse,
  type DeleteSessionResponse,
  type ISessionsAdapter,
} from './sessions';

// Citations domain
export {
  CitationTypeEnum,
  type CitationType,
  CitationStrengthEnum,
  type CitationStrength,
  CitationStatusEnum,
  type CitationStatus,
  CitationDTOSchema,
  type CitationDTO,
  CitationMetricsDTOSchema,
  type CitationMetricsDTO,
  AISuggestionDTOSchema,
  type AISuggestionDTO,
  DuplicateAlertDTOSchema,
  type DuplicateAlertDTO,
  type CitationListFilters,
  type CitationSortField,
  type CitationListParams,
  type CreateCitationRequest,
  type UpdateCitationRequest,
  type CitationListResponse,
  type CitationDetailResponse,
  type CreateCitationResponse,
  type UpdateCitationResponse,
  type DeleteCitationResponse,
  type CitationMetricsResponse,
  type AISuggestionsResponse,
  type DuplicateAlertsResponse,
  type ICitationsAdapter,
  type CitationExportParams,
} from './citations';

// Notes domain
export {
  type NoteDTO,
  type NoteSortField,
  type NoteListFilters,
  type NoteListParams,
  type CreateNoteRequest,
  type UpdateNoteRequest,
  type NoteListResponse,
  type NoteDetailResponse,
  type CreateNoteResponse,
  type UpdateNoteResponse,
  type DeleteNoteResponse,
  dtoToNote,
} from './notes';

// Audit domain
export {
  type AuditStatus,
  type AuditActorType,
  type AuditEventDTO,
  type AuditFilters,
  type AuditListResponse,
  type IAuditAdapter,
} from './audit';

// Shares domain
export {
  ShareResourceTypeEnum,
  type ShareResourceType,
  ShareStatusEnum,
  type ShareStatus,
  SharingConfigSchema,
  type SharingConfig,
  PublicSharingSchema,
  type PublicSharing,
  CollaboratorSchema,
  type Collaborator,
  ShareDTOSchema,
  type ShareDTO,
  SharedContentDataSchema,
  SharedContentResponseSchema,
  SendRecipientResultSchema,
  SendShareResponseSchema,
  type CreateShareRequest,
  type UpdateShareRequest,
  type VerifySharePasswordRequest,
  type ShareListFilters,
  type ShareSortField,
  type ShareListParams,
  type ShareListResponse,
  type ShareDetailResponse,
  type CreateShareResponse,
  type UpdateShareResponse,
  type RevokeShareResponse,
  type VerifyPasswordResponse,
  type SharedContentData,
  type SharedContentResponse,
  type SendSharePeopleRequest,
  type SendSharePeopleResponse,
  type SendShareRecipient,
  type SendShareRecipientResult,
  type ISharesAdapter,
  type ShareComment,
  type CreateShareCommentRequest,
  type UpdateShareCommentRequest,
  type ShareCommentsResponse,
  type SaveFromShareRequest,
  type SaveFromShareResponse,
  type ShareHighlight,
} from './shares';

// Intelligence domain (RFC-012)
export {
  SearchModeEnum,
  type SearchMode,
  FeedbackActionEnum,
  type FeedbackAction,
  FeedbackSurfaceEnum,
  type FeedbackSurface,
  SuggestionStatusEnum,
  type SuggestionStatus,
  type WorkspaceContextCreateRequest,
  type WorkspaceContextUpdateRequest,
  type WorkspaceContextDTO,
  type SearchFiltersRequest,
  type IntelligenceSearchRequest,
  type RelevanceExplanation,
  type SearchResultDTO,
  type IntelligenceSearchResponse,
  type SuggestionReasonDTO,
  type SuggestionDTO,
  type SuggestionListResponse,
  type FeedbackCreateRequest,
  type FeedbackAcknowledgeDTO,
  type FeedbackDTO,
  type TrendingClusterDTO,
  type TrendingListResponse,
  type ScoringWeightDTO,
  type IntelligenceDashboardDTO,
  type IIntelligenceAdapter,
} from './intelligence';

// Graph domain (RFC-003)
export {
  type GraphNodeType,
  type GraphEdgeType,
  type CreatedBySource,
  type GraphEdgeDTO,
  type GraphNeighbourDTO,
  type CreateGraphEdgeRequest,
  type GraphEdgeListParams,
  type GraphNeighbourParams,
  type GraphEdgeListResponse,
  type GraphNeighbourListResponse,
  type CitesMetadata,
  type MentionsMetadata,
  type ExtractedFromMetadata,
  type EmbedsMetadata,
  type DerivesFromMetadata,
  type CommentsOnRangeMetadata,
} from './graph';

// Snapshots domain (RFC-003)
export {
  type NoteSnapshotDTO,
  type NoteSnapshotDetailDTO,
  type NoteSnapshotRestoreDTO,
  type SnapshotListParams,
  type SnapshotListResponse,
} from './snapshot';

// Notifications domain
export {
  type NotificationCategory,
  type NotificationType,
  type NotificationPriority,
  type NotificationDeliveryChannel,
  type NotificationActorDTO,
  type NotificationDTO,
  type UnreadCountDTO,
  type NotificationListParams,
  type MarkReadRequest,
  type MarkAllReadRequest,
  type DismissRequest,
  type NotificationPreferenceDTO,
  type NotificationPreferencesDTO,
  type NotificationPreferenceUpdateRequest,
  type NotificationPreferencesBulkUpdateRequest,
  type NotificationListResponse,
  type SuccessResponseDTO,
  type INotificationsAdapter,
  type DismissAllRequest,
} from './notifications';

// Evidence domain (Phase 3 — claim-level intelligence)
export {
  EvidenceTypeBucketSchema,
  type EvidenceTypeBucket,
  DirectionBucketSchema,
  type DirectionBucket,
  TopClaimReadSchema,
  type TopClaimRead,
  EvidenceSynthesisReadSchema,
  type EvidenceSynthesisRead,
  ClaimRowReadSchema,
  type ClaimRowRead,
  type ClaimsListParams,
  type ClaimsListResponse,
} from './evidence';

// Citation Intelligence domain (Phase 4 + Phase 5)
export {
  CitationEntryReadSchema,
  type CitationEntryRead,
  CitationGraphReadSchema,
  type CitationGraphRead,
  CoCitationPairReadSchema,
  type CoCitationPairRead,
  CoCitationListReadSchema,
  type CoCitationListRead,
  RecommendationReadSchema,
  type RecommendationRead,
  RecommendationListReadSchema,
  type RecommendationListRead,
  type RecommendationRequestParams,
  ContradictionPairReadSchema,
  type ContradictionPairRead,
  ContradictionListReadSchema,
  type ContradictionListRead,
  ContradictionSummarySchema,
  type ContradictionSummary,
  ResearchGapReadSchema,
  type ResearchGapRead,
  ResearchGapListReadSchema,
  type ResearchGapListRead,
  ResearchGapSummarySchema,
  type ResearchGapSummary,
  TopCoCitationListReadSchema,
  type TopCoCitationListRead,
} from './citation-intelligence';

// Compare Boards domain
export {
  CompareBoardReadSchema,
  type CompareBoardRead,
  CompareBoardCreateSchema,
  type CompareBoardCreate,
  CompareBoardPatchSchema,
  type CompareBoardPatch,
  type CompareBoardListResponse,
} from './compare-boards';

// API Keys domain
export {
  ApiKeyCreateSchema,
  type ApiKeyCreate,
  ApiKeyCreatedSchema,
  type ApiKeyCreated,
  ApiKeyReadSchema,
  type ApiKeyRead,
  ApiKeyListSchema,
  type ApiKeyList,
} from './api-keys';

// Billing domain
export {
  BillingModeSchema,
  type BillingMode,
  EnforcementModeSchema,
  type EnforcementMode,
  PlanTierSchema,
  type PlanTier,
  SubscriptionStatusSchema,
  type SubscriptionStatus,
  LimitsReadSchema,
  type LimitsRead,
  BillingPlanReadSchema,
  type BillingPlanRead,
  BillingSubscriptionReadSchema,
  type BillingSubscriptionRead,
  BillingPlanResponseSchema,
  type BillingPlanResponse,
  AiBucketUsageSchema,
  type AiBucketUsage,
  ExtractionBucketUsageSchema,
  type ExtractionBucketUsage,
  StorageBucketUsageSchema,
  type StorageBucketUsage,
  BucketsUsageSchema,
  type BucketsUsage,
  SeatsUsageSchema,
  type SeatsUsage,
  UsageResponseSchema,
  type UsageResponse,
  UsageHistoryResponseSchema,
  type UsageHistoryResponse,
  CreateCheckoutSessionRequestSchema,
  type CreateCheckoutSessionRequest,
  CheckoutSessionResponseSchema,
  type CheckoutSessionResponse,
  BillingPortalResponseSchema,
  type BillingPortalResponse,
  SeatUpdateRequestSchema,
  type SeatUpdateRequest,
  SeatUpdateResponseSchema,
  type SeatUpdateResponse,
  PlanCatalogFeaturesSchema,
  type PlanCatalogFeatures,
  PlanCatalogLimitsSchema,
  type PlanCatalogLimits,
  PlanCatalogEntrySchema,
  type PlanCatalogEntry,
  PlanCatalogResponseSchema,
  type PlanCatalogResponse,
  FeatureMatrixValueSchema,
  type FeatureMatrixValue,
  FeatureMatrixFeatureSchema,
  type FeatureMatrixFeature,
  FeatureMatrixCategorySchema,
  type FeatureMatrixCategory,
  CatalogMetadataSchema,
  type CatalogMetadata,
  type AddOnType,
  type AddOnStatus,
  type AddOnCatalogEntry,
  type AddOnRead,
  type ActiveAddOnsResponse,
  type AddOnCheckoutResponse,
  type AddOnRecommendationDTO,
} from './billing';

// Settings domain
export {
  ProfileSettingsReadSchema,
  type ProfileSettingsRead,
  ProfileSettingsUpdateSchema,
  type ProfileSettingsUpdate,
  AiSettingsReadSchema,
  type AiSettingsRead,
  NotificationSettingsReadSchema,
  type NotificationSettingsRead,
  NotificationSettingsUpdateSchema,
  type NotificationSettingsUpdate,
  PrivacySettingsReadSchema,
  type PrivacySettingsRead,
  PrivacySettingsUpdateSchema,
  type PrivacySettingsUpdate,
  UserSettingsReadSchema,
  type UserSettingsRead,
  DataExportReadSchema,
  type DataExportRead,
  AccountDeletionResponseSchema,
  type AccountDeletionResponse,
} from './settings';

// Note Comments domain (inline discussions on notes)
export {
  NoteCommentDTOSchema,
  type NoteCommentDTO,
  CreateCommentRequestSchema as NoteCreateCommentRequestSchema,
  type CreateCommentRequest as NoteCreateCommentRequest,
  UpdateCommentRequestSchema as NoteUpdateCommentRequestSchema,
  type UpdateCommentRequest as NoteUpdateCommentRequest,
  type NoteCommentListResponse,
} from './note-comments';

// Seeker — Search Microservice (manual schemas, no OpenAPI spec yet)
export {
  SeekerTabEnum,
  SeekerTabSchema,
  type SeekerTab,
  SeekerSortSchema,
  type SeekerSort,
  SeekerFeedbackActionSchema,
  type SeekerFeedbackAction,
  type SeekerSearchFilters,
  type SeekerSearchRequest,
  type SeekerFeedbackRequest,
  SeekerSearchResultSchema,
  type SeekerSearchResult,
  SeekerFacetCountsSchema,
  type SeekerFacetCounts,
  SeekerClaimResultSchema,
  type SeekerClaimResult,
  SeekerSearchResponseSchema,
  type SeekerSearchResponse,
  SeekerSuggestResponseSchema,
  type SeekerSuggestResponse,
  SeekerFeedbackRequestSchema,
} from './search-seeker';

// Discovery — External Research Engine
export {
  DiscoverySourceEnum,
  type DiscoverySource,
  WorkspaceItemStatusEnum,
  type WorkspaceItemStatus,
  type DiscoveryFilters,
  type DiscoverySearchRequest,
  type ProviderMeta,
  type DiscoveryResultDTO,
  type DiscoverySearchResponse,
  type SaveItemRequest,
  type SaveItemResponse,
  type WorkspaceItemDTO,
  type WorkspaceItemsListResponse,
  type SavedIdsResponse,
  type IDiscoveryAdapter,
  // Import with PDF
  type ImportItemResponse,
  type BatchImportWithPdfRequest,
  type BatchImportWithPdfResponse,
  type SaveAndImportRequest,
  canResolvePdf,
  // Relevance scoring
  type RelevanceScoreRequest,
  type RelevanceScoreResponse,
  type RelevanceScoreResult,
  // Processing mode
  type ProcessingMode,
  // Synthesis SSE events
  type SynthesisItem,
  type SynthesisTokenEvent,
  type SynthesisCompleteEvent,
  type SynthesisErrorEvent,
} from './discovery';

// Chat domain (conversations, messages, SSE events, models)
export {
  type TransparencySettings,
  type ChatRunStartedEvent,
  type EvidenceReadyItem,
  type ChatEvidenceSufficiencyEvent,
  type ChatAgentHopEvent,
  type ClaimConfidence,
  type ChatEvidenceReadyEvent,
  type ChatTokenEvent,
  type ChatCitationEvent,
  type ChatIntegrityUpdateEvent,
  type ChatRunCompletedEvent,
  type ChatErrorEvent,
  type ChatEvidenceDirectionsEvent,
  type ChatFollowUpsEvent,
  type ChatReasoningStepEvent,
  type KnowledgeSource,
  type ChatKnowledgeSourceEvent,
  type ChatSSEEventMap,
  type ChatScope,
  type PinnedEvidence,
  type AIProfileOverride,
  type ChatSendRequest,
  type ConversationListItem,
  type MessageRead,
  type CursorPage,
  type ConversationListResponse,
  type MessageListResponse,
  type CitationRead as ChatCitationRead,
  type EvidenceItemRead,
  type ModelCapabilities,
  type ModelMetadata,
  type ModelsListResponse,
  type RunTimings,
  type RunDebugRead,
} from './chat';

// Home Board (Research Command Center) — Slices 1–4 envelope + discriminated-union surfaces
export {
  type HomeBoardResponse,
  type HomeWorkspaceDTO,
  type ObjectiveDTO,
  type HomeSurface,
  type HomeSurfaceKind,
  type WorkspaceIdentitySurface,
  type PipelinePulseSurface,
  type CriticalBlockersSurface,
  type CriticalBlockerItem,
  type AttentionItemsSurface,
  type AttentionItem,
  type AttentionItemReasonType,
  type ActiveWorkSurface,
  type ActiveWorkPipelineStage,
  type RecentReadySurface,
  type RecentReadyItem,
  type EvidenceHighlightsSurface,
  type EvidenceHighlightItem,
  type EvidenceHighlightType,
  type FingerprintStatusSurface,
  type DiscoverySuggestionsSurface,
  type DiscoverySuggestionItem,
  type DiscoverySavedInboxSurface,
  type SavedInboxItem,
  type SavedInboxItemStatus,
  type SavedInboxSource,
  type IntelligenceCriticalSurface,
  type CriticalInsightItem,
  type CriticalInsightType,
  type IntelligencePatternsSurface,
  type PatternItem,
  type PatternType,
  type PatternTier,
  type RecentConversationsSurface,
  type RecentConversationItem,
  type RecentConversationScopeMode,
  type NotesActivitySurface,
  type NotesActivityItem,
  type NotesUnconfirmedEdgesSurface,
  type UnconfirmedEdgeItem,
  type UnconfirmedEdgeType,
  type UnconfirmedEdgeSource,
  type NotesConnectedArtifactsSurface,
  type ConnectedArtifactItem,
  type ConnectedArtifactKind,
  type PinnedCollectionsSurface,
  type PinnedCollectionItem,
  HomeBoardResponseSchema,
  HomeWorkspaceDTOSchema,
  ObjectiveDTOSchema,
  isWorkspaceIdentity,
  isPipelinePulse,
  isCriticalBlockers,
  isAttentionItems,
  isActiveWork,
  isRecentReady,
  isEvidenceHighlights,
  isFingerprintStatus,
  isDiscoverySuggestions,
  isDiscoverySavedInbox,
  isIntelligenceCritical,
  isIntelligencePatterns,
  isRecentConversations,
  isNotesActivity,
  isNotesUnconfirmedEdges,
  isNotesConnectedArtifacts,
  isPinnedCollections,
} from './home';

// Systematic Reviews domain (VIZ-RFC-0012)
export {
  // Enum schemas (Zod)
  SRStatusEnum,
  SRMemberRoleEnum,
  SRDecisionEnum,
  SRScreeningStageEnum,
  SRRecordStatusEnum,
  SRConflictStatusEnum,
  SRRecordSourceEnum,
  SRQuestionFrameworkEnum,
  SRCriterionTypeEnum,
  SRFinalDecisionEnum,
  SRResolutionTypeEnum,
  // Enum types
  type SRStatus,
  type SRMemberRole,
  type SRDecision,
  type SRScreeningStage,
  type SRRecordStatus,
  type SRConflictStatus,
  type SRRecordSource,
  type SRQuestionFramework,
  type SRCriterionType,
  type SRFinalDecision,
  type SRResolutionType,
  // DTO types
  type SRProjectRead,
  type SRProjectCreate,
  type SRProjectUpdate,
  type SRMemberRead,
  type SRRecordRead,
  type SRRecordListItem,
  type SRDecisionRead,
  type SRConflictRead,
  type SRScreeningStats,
  type SRExclusionBreakdown,
  type SRExclusionAnalysis,
  type SRExclusionReasonCount,
  type FunnelSummary,
  type FunnelStageScreening,
  type FunnelStageSynthesis,
  type SRAdvanceStatus,
  // Param types
  type SRListParams,
  type SRRecordListParams,
  type SRListResponse,
  type SRRecordListResponse,
  // Adapter interface
  type ISystematicReviewsAdapter,
} from './systematic-reviews';
